import { Spinner } from "./Spinner.js"
import { Toast } from "./Toast.js"
import { I18N } from "./I18N.js"
import { Messaging } from "./lib/Messaging.js"
import { ReceiptPreviewDialog } from "./ReceiptPreviewDialog"
import { Order } from "./data/Order.js"

export class Confirmation {
	_target
	_appContext
	_processInstanceId
	_orderObject = new Order()
	_storesPromise
	_store
	_loadedByUrl
	_cancelTaskId
	_onNotAcceptedHandlers = []
	_toastsToDismiss = []
	_notAcceptedToast = null
	_messaging
	_subscriptions = new Set()

	constructor(target) {
		this._target = document.getElementById(target)
		this._target.setAttribute('hidden', '')
		this._target.innerHTML = `
			<h2><span id="confirmationSyncIcon" class="material-icons sync animated">sync</span><span i18n>Confirmation</span></h2>
			<div><label ><span id="deliveryEventType"></span>:</label> <div><strong id="deliveryDate">--</strong></div></div>
			<div id="orderIdWrapper"><label><span i18n>Order ID</span>:</label><div><strong id="orderId">--</strong></div></div>
			<div id="invoiceNumberWrapper" hidden>
				<label><span i18n>Invoice Number</span>:</label>
				<div class="btnGroup">
					<div><strong id="invoiceNumber">--</strong></div>
					<button id="invoicePreviewBtn" title="${I18N.translate('Receipt preview')}"><span class="material-icons-outlined">preview</span></button>
				</div>
			</div>
			<div><label><span i18n>Order State</span>:</label><div><strong id="currentTasks">--</strong></div></div>
			<div class="infoblock">
				<div>Bis gleich!</div>
				<div>Dein <span id="confirmationStoreName">--</span></div>
				<div>
					<span id="confirmationStoreAddress">--</span><br>
					<a id="confirmationStorePhone">--</a><br>
					<a id="confirmationStoreMail">--</a>
				</div>
			</div>
			<div class="btnGroup">
				<button id="updateConfirmationBtn" class="primary" disabled i18n>Refresh</button>
				<button id="cancelBtn" disabled i18n>Cancel Order</button>
			</div>
			<div id="receiptPreview"></div>
		`
		I18N.translateElement(this._target)
		this._messaging = new Messaging()
		document.getElementById('updateConfirmationBtn').addEventListener('click', () => {
			this._fetchVariables()
			this._fetchActiveTasks()
		})
		// Cancellation
		document.getElementById('cancelBtn').addEventListener('click', () => {
			if (this._cancelTaskId) {
				fetch(`/cancel-order/${this._processInstanceId}`, {
					method: 'POST',
					mode: 'cors',
					cache: 'no-cache',
					credentials: 'same-origin',
					referrerPolicy: 'no-referrer'
				})
					.then(response => {
						if (response.ok) {
							// Don't receive further events after cancellation has succeeded
							this._messaging.unSubscribe(`/order/${this._processInstanceId}/updates`)
							this._subscriptions.delete(`/order/${this._processInstanceId}/updates`)
							document.getElementById('confirmationSyncIcon').classList.remove('animated')

							this._cancelTaskId = null
							document.getElementById('cancelBtn').setAttribute('disabled', '')
							document.getElementById('updateConfirmationBtn').setAttribute('disabled', '')
							document.getElementById('currentTasks').innerHTML = I18N.translate('cancelled')
							this._toastsToDismiss.push(new Toast('toaster', I18N.translate('Order cancelled'), Toast.INFO, 15))
						}
						else {
							this._cancelTaskId = null
							document.getElementById('cancelBtn').setAttribute('disabled', '')
							console.error('Cancellation failed:', response)
							this._toastsToDismiss.push(new Toast('toaster', I18N.translate('Cancellation failed!'), Toast.ERROR, 15))
						}
					})
					.catch(e => {
						console.error('Cancellation failed:', e)
						this._toastsToDismiss.push(new Toast('toaster', I18N.translate('Cancellation failed!'), Toast.ERROR, 15))
					})
			}
			else {
				this._toastsToDismiss.push(new Toast('toaster', I18N.translate('Cancellation is no longer possible!'), Toast.WARN, 15))
			}
		})
		// The Invoice preview button
		document.getElementById('invoicePreviewBtn').addEventListener('click', () => {
			new ReceiptPreviewDialog('receiptPreview', this._orderObject, this._store).setVisible(true)
		})

	}

	setAppContext = appContext => this._appContext = appContext

	setProcessInstanceId = processInstanceId => {
		this._processInstanceId = processInstanceId
		this._orderObject._id = processInstanceId
		const history = JSON.parse(localStorage.getItem('orderHistory') || '[]')
		if (!history.some(e => e.id === processInstanceId)) {
			history.unshift({ id: processInstanceId, timestamp: Date.now() })
			if (history.length > 20) history.pop()
			localStorage.setItem('orderHistory', JSON.stringify(history))
		}
	}

	setOrderVO = orderVO => {
		this._orderObject.setOrder(orderVO)
		this._storesPromise
			.then(stores => {
				this.setStore(stores.find(s => s.id === this._orderObject._storeId))
			})
	}

	setCartVO = cartVO => {
		this._orderObject.setCart(cartVO)
	}

	setStoresPromise = storesPromise => {
		this._storesPromise = storesPromise
	}

	setStore = store => {
		this._store = store
		// TODO store in this._orderObject
	}

	setLoadedByUrl = loadedByUrl => this._loadedByUrl = loadedByUrl

	addOnNotAcceptedHandlers = (...onNotAcceptedHandlers) => this._onNotAcceptedHandlers.push(...onNotAcceptedHandlers)

	setVisible = visible => {
		if (visible) {
			this._target.removeAttribute('hidden')
			if (this._notAcceptedToast) {
				this._notAcceptedToast.dismiss()
			}
			// Restart update mechanism
			this._pollState(false, 'Restart for set visible')
			this._pollState(true)
			document.getElementById('updateConfirmationBtn').removeAttribute('disabled')
		}
		else {
			this._toastsToDismiss.forEach(toast => toast.dismiss())
			this._toastsToDismiss = []
			this._target.setAttribute('hidden', '')
			this._pollState(false, 'Set visible = false')
		}
	}

	_renderValues = () => {
		document.getElementById('orderId').innerHTML = this._orderObject.getIdString()
		document.getElementById('invoiceNumber').innerHTML = this._orderObject.getInvoiceNumber()
		if (this._orderObject.getInvoiceNumber()) {
			document.getElementById('invoiceNumberWrapper').removeAttribute('hidden')
			document.getElementById('orderIdWrapper').setAttribute('hidden', '')
		}
		else {
			document.getElementById('orderIdWrapper').removeAttribute('hidden')
			document.getElementById('invoiceNumberWrapper').setAttribute('hidden', '')
		}
		document.getElementById('deliveryDate').innerHTML = this._orderObject.getDeliveryDateTime(false)
		document.getElementById('confirmationStoreName').innerHTML = this._orderObject.getStoreName()
		document.getElementById('confirmationStoreAddress').innerHTML = this._orderObject.getStoreAddressString()
		document.getElementById('confirmationStorePhone').innerHTML = this._orderObject.getStorePhone()
		document.getElementById('confirmationStorePhone').setAttribute('href', `tel:${this._orderObject.getStorePhone()}`)
		document.getElementById('confirmationStoreMail').innerHTML = this._orderObject.getStoreEMail()
		document.getElementById('confirmationStoreMail').setAttribute('href', `mailto:${this._orderObject.getStoreEMail()}`)
		document.getElementById('deliveryEventType').innerHTML = I18N.translate(this._orderObject.isDelivery() ? 'Delivery date' : 'Pickup date')
	}

	/**
	 * Fetch variables and active tasks and establish updates over web sockets
	 * 
	 * @param {*} enabled 
	 * @param {*} reason 
	 */
	_pollState = (enabled, reason) => {
		if (enabled) {
			this._fetchVariables()
			this._fetchActiveTasks()
			this._messaging.connect(
				() => {
					this._updatePausedToast = new Toast('toaster', I18N.translate('The automatic update will continue...'), Toast.WARN, 30)
					this._toastsToDismiss.push(this._updatePausedToast)
					document.getElementById('confirmationSyncIcon').classList.remove('animated')
				},
				() => {
					if (this._updatePausedToast) {
						this._updatePausedToast.dismiss()
						this._updatePausedToast = null
					}
					this._toastsToDismiss.push(new Toast('toaster', I18N.translate('Auto updates resumed.'), Toast.INFO, 5))
					document.getElementById('confirmationSyncIcon').classList.add('animated')
					this._isProcessAlivePromise()
						.then(() => {
							this._fetchVariables()
							this._fetchActiveTasks()
						})
						.catch(result => {
							if (result === false) {
								//Process already closed
								this._toastsToDismiss.push(new Toast('toaster', I18N.translate('The process has alreday terminated'), Toast.INFO, 15))
								this._pollState(false, 'PROCESS_ENDED')
								document.getElementById('currentTasks').innerHTML = I18N.translate('Ended')
								document.getElementById('updateConfirmationBtn').setAttribute('disabled', '')
								document.getElementById('cancelBtn').setAttribute('disabled', '')
								if (this._loadedByUrl) {
									this._appContext.setActive('stores')
									localStorage.removeItem('processInstanceId')
								}
							}
						})
				}
			)
				.then(() => {
					console.debug('WebSocket for updates connected.')
					if (!this._subscriptions.has(`/order/${this._processInstanceId}/updates`)) {
						this._messaging.subscribe(`/order/${this._processInstanceId}/updates`, message => this._onMessage(message))
						this._subscriptions.add(`/order/${this._processInstanceId}/updates`)
						console.debug('Subscribed to topic: ', `/order/${this._processInstanceId}/updates`)
					}
					document.getElementById('confirmationSyncIcon').classList.add('animated')
				})
				.catch(e => {
					console.error(e)
					this._toastsToDismiss.push(new Toast('toaster', I18N.translate('We have problems to start the automatic update. Use the "Update" button to update the status of your order.'), Toast.WARN, 30))
				})
		}
		else {
			console.log('Closing WebSocket because of ', reason)
			document.getElementById('confirmationSyncIcon').classList.remove('animated')
			this._subscriptions.forEach(subscription => {
				this._messaging.unSubscribe(subscription)
			})
			this._subscriptions.clear()
			this._messaging.close()
		}
	}

	/**
	 * Fetches variables and renders UI
	 * 
	 */
	_fetchVariables = () => {
		const spinner = new Spinner(this._target, 15)
		fetch(`/engine-rest/process-instance/${this._processInstanceId}/variables`, {
			method: 'GET',
			mode: 'cors', // no-cors, *cors, same-origin
			cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
			credentials: 'same-origin', // include, *same-origin, omit
			referrerPolicy: 'no-referrer' // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
		})
			.then(variablesResponse => {
				if (variablesResponse.ok) {
					variablesResponse.json()
						.then(variables => {
							spinner.dismiss()
							// TODO Remove this log:
							console.debug('Response: ', variables)
							this._orderObject.setVariables(variables)
							this._storesPromise
								.then(stores => {
									this.setStore(stores.find(s => s.id === this._orderObject._storeId))
								})
							this._renderValues()
						})
				}
				else {
					// Response not OK
					spinner.dismiss()
					console.error('Response: ', variablesResponse)
					variablesResponse.json()
						.then(variablesError => {
							if (variablesError.cause.includes('execution') && variablesError.cause.includes('doesn\'t exist')) {
								this._toastsToDismiss.push(new Toast('toaster', I18N.translate('The process has alreday terminated'), Toast.ERROR))
								document.getElementById('currentTasks').innerHTML = I18N.translate('ended')
								document.getElementById('updateConfirmationBtn').setAttribute('disabled', '')
								document.getElementById('cancelBtn').setAttribute('disabled', '')
								if (this._loadedByUrl) {
									this._appContext.setActive('stores')
									localStorage.removeItem('processInstanceId')
								}
								this._renderValues()
							}
							else {
								this._toastsToDismiss.push(new Toast('toaster', variablesError.cause, Toast.ERROR))
							}
						})
						.catch(e => this._toastsToDismiss.push(new Toast('toaster', e, Toast.ERROR)))
				}
			})
			.catch(e => {
				// Fetch failed
				spinner.dismiss()
				console.error(e)
				this._toastsToDismiss.push(new Toast('toaster', e, Toast.ERROR))
			})
	}

	/**
	 * Fetches active tasks and rerenders UI
	 */
	_fetchActiveTasks = () => {
		const spinner = new Spinner(this._target, 15)
		fetch(`/engine-rest/task?processInstanceId=${this._processInstanceId}`, {
			method: 'GET',
			mode: 'cors', // no-cors, *cors, same-origin
			cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
			credentials: 'same-origin', // include, *same-origin, omit
			referrerPolicy: 'no-referrer' // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
		})
			.then(tasksResponse => {
				if (tasksResponse.ok) {
					tasksResponse.json()
						.then(tasks => {
							spinner.dismiss()
							// TODO Remove this log:
							console.debug('Get Tasks Response: ', tasks)
							document.getElementById('currentTasks').innerHTML = tasks.map(t => I18N.translate(t.name)).join(', ')
							const waitForCancelTask = tasks.find(t => t.name === 'Auftragsbestätigung')
							if (waitForCancelTask) {
								this._cancelTaskId = waitForCancelTask.id
								document.getElementById('cancelBtn').removeAttribute('disabled')
							}
							else {
								this._cancelTaskId = null
								document.getElementById('cancelBtn').setAttribute('disabled', '')
							}
							this._renderValues()
						})
				}
				else {
					// Response not OK
					spinner.dismiss()
					console.error('Response: ', tasksResponse)
					tasksResponse.json()
						.then(variablesError => {
							if (variablesError.cause.includes('execution') && variablesError.cause.includes('doesn\'t exist')) {
								this._toastsToDismiss.push(new Toast('toaster', I18N.translate('The process has alreday terminated'), Toast.ERROR))
								document.getElementById('currentTasks').innerHTML = I18N.translate('ended')
								document.getElementById('updateConfirmationBtn').setAttribute('disabled', '')
								document.getElementById('cancelBtn').setAttribute('disabled', '')
								if (this._loadedByUrl) {
									this._appContext.setActive('stores')
									localStorage.removeItem('processInstanceId')
								}
							}
							else {
								this._toastsToDismiss.push(new Toast('toaster', variablesError.cause, Toast.ERROR))
							}
						})
						.catch(e => this._toastsToDismiss.push(new Toast('toaster', e, Toast.ERROR)))
				}
			})
			.catch(e => {
				// Fetch failed
				spinner.dismiss()
				console.error(e)
				this._toastsToDismiss.push(new Toast('toaster', e, Toast.ERROR))
			})
	}

	_onMessage = updates => {
		console.debug(updates)
		if (updates.paid === 'true') {
			this._toastsToDismiss.push(new Toast('toaster', I18N.translate('Payment made'), Toast.INFO, 7))
		}
		else if (updates.paid === 'false') {
			new Toast('toaster', I18N.translate('Payment refunded'), Toast.INFO)
		}
		if (updates.receiveDateTime) {
			const receiveDate = new Date(updates.receiveDateTime)
			this._orderObject._receiveDate = receiveDate
		}
		if (updates.deliveryDateTime) {
			const deliveryDate = new Date(updates.deliveryDateTime)
			this._orderObject._deliveryDate = deliveryDate
		}
		if (updates.invoiceNumber) {
			const rawInvoiceNumberString = String(updates.invoiceNumber)
			this._orderObject._invoiceNumber = rawInvoiceNumberString
		}
		if (updates.completed === 'true') {
			this._toastsToDismiss.push(new Toast('toaster', I18N.translate('The process is completed'), Toast.INFO, 15))
			document.getElementById('currentTasks').innerHTML = I18N.translate('completed')
			this._pollState(false, 'ORDER_COMPLETED')
			document.getElementById('updateConfirmationBtn').setAttribute('disabled', '')
			document.getElementById('cancelBtn').setAttribute('disabled', '')
			if (localStorage.getItem('processInstanceId') === this._processInstanceId) {
				localStorage.removeItem('processInstanceId')
			}
		}
		else if (updates.accepted === 'timeout') {
			this._notAcceptedToast = new Toast('toaster', I18N.translate('The order has not been accepted. Please try again later or contact the store by phone.'), Toast.WARN)
			document.getElementById('currentTasks').innerHTML = I18N.translate('Order not accepted')
			this._pollState(false, 'ORDER_NOT_ACCEPTED')
			document.getElementById('updateConfirmationBtn').setAttribute('disabled', '')
			document.getElementById('cancelBtn').setAttribute('disabled', '')
			this._onNotAcceptedHandlers.forEach(handler => handler())
			this._appContext.setActive('cart')
		}
		else if (updates.cancelled === 'true' || updates.accepted === 'cancelled') {
			const rejectionMessage = updates.rejectionReason
				? `${I18N.translate('The store has rejected this order!')} ${I18N.translate('Reason')}: ${updates.rejectionReason}`
				: I18N.translate('The store has rejected this order!')
			this._toastsToDismiss.push(new Toast('toaster', rejectionMessage, Toast.WARN, 30))
			document.getElementById('currentTasks').innerHTML = updates.rejectionReason
				? `${I18N.translate('Order cancelled')} (${updates.rejectionReason})`
				: I18N.translate('Order cancelled')
			this._pollState(false, 'ORDER_CANCELLED')
			document.getElementById('updateConfirmationBtn').setAttribute('disabled', '')
			document.getElementById('cancelBtn').setAttribute('disabled', '')
		}
		else {
			// Wait a moment and then fetch active tasks
			setTimeout(this._fetchActiveTasks, 500)
		}
		this._renderValues()
	}

	_isProcessAlivePromise = () => {
		return new Promise((resolve, reject) => {
			fetch(`/engine-rest/process-instance/${this._processInstanceId}/variables/receiveDateTime`, {
				method: 'GET',
				mode: 'cors', // no-cors, *cors, same-origin
				cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
				credentials: 'same-origin', // include, *same-origin, omit
				referrerPolicy: 'no-referrer' // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
			})
				.then(variablesResponse => {
					if (variablesResponse.ok) {
						resolve(true)
					}
					else {
						reject(false)
					}
				})
				.catch(e => {
					console.info(e)
					reject(e)
				})
		})
	}

	_formatDeliveryDate = date => {
		const displayDate = new Date(date.getTime() + (date.getSeconds() > 0 ? 60 * 1000 : 0))
		let dateString
		if (displayDate.getDate() === new Date().getDate()) {
			// Today
			dateString = displayDate.toLocaleTimeString()
		}
		else {
			// Tomorrow or later
			dateString = displayDate.toLocaleString()
		}
		// Cut seconds
		return dateString.substring(0, dateString.length - 3)
	}

}